/**
 * Peer Advisory program API.
 *
 * Kept in its own module rather than added to api.ts (already ~6000 lines),
 * following the precedent set by paymentsApi.ts: a new surface stays reviewable
 * on its own.
 *
 * The backend serves these as snake_case (schemas/peer_advisory_program.py uses
 * plain BaseModel, matching the Expert Program schemas rather than the
 * CamelModel used by the OKR/session endpoints), so the types below are
 * snake_case too. Do not "fix" that to camelCase without changing the backend
 * schemas -- the two Expert/Peer program surfaces deliberately share one rule.
 *
 * Sessions, goals and payments are NOT here: those reuse the existing
 * type-agnostic endpoints (programsApi.getSessions/createSession, the OKR
 * endpoints, paymentsApi) because program_meetings / objective_key_results /
 * payments are all keyed on program_id and work for any program type.
 */

import { authAxios } from "@/services/axiosInstance";
import { getEnvVar } from "@/utils/util";

const API_BASE = getEnvVar("API_BASE");
const PEER_BASE = `${API_BASE}/peer-advisory-programs`;

// ── Types ─────────────────────────────────────────────────────────────────────

export type PeerVisibility = "PUBLIC" | "INVITE_ONLY" | "ORG_CODE";
export type PeerSessionMode = "SINGLE" | "MULTI";
export type PeerFacilitationMode = "SELF" | "INVITED" | "REQUEST_FA";
/**
 * DRAFT is the applicant's own unfinished form. It is invisible to the
 * facilitator (the backend's QUEUE_VISIBLE_STATUSES excludes it), so it only
 * ever appears in the applicant's own views.
 *
 * WAITLISTED and INFO_REQUESTED were always in the backend enum but had no
 * client type, so a waitlisted application read as an unknown status.
 */
export type PeerApplicationStatus =
  | "DRAFT"
  | "SUBMITTED"
  | "ACCEPTED"
  | "WAITLISTED"
  | "INFO_REQUESTED"
  | "REJECTED"
  | "WITHDRAWN";

/** The six steps of the achiever's application, in order. */
export type PeerApplyStep = "fit" | "application" | "goals" | "availability" | "agreement" | "review";

/**
 * The five Audience & Selection criteria. Each list holds option values from a
 * master_data scope the expert profile already uses, so they line up with the
 * expert taxonomy:
 *   functions  -> profile::advisor-primary-function  ("Role")
 *   levels     -> profile::advisor-level             ("Seniority")
 *   industries -> profile::core-industries           ("Industry")
 *   org_types  -> profile::organisation-type         ("Company Stage")
 */
export interface AudienceCriteria {
  functions: string[];
  levels: string[];
  industries: string[];
  org_types: string[];
  /**
   * The fifth criterion ("Years of Experience"). A RANGE STRING from
   * ACHIEVER_EXPERIENCES (e.g. "5-10 years"), not a number — it shares its
   * vocabulary with user_profiles.years_of_experience so a group's stated range
   * and a member's profile range can be compared for matching later.
   * null / absent = "No minimum".
   */
  years_of_experience?: string | null;
}

/**
 * One entry in a reorderable JSONB list. `id` is stable across edits, which is
 * why these are objects rather than bare strings -- editing item 2 must not
 * rewrite the whole array.
 */
export interface ChallengeItem {
  id?: string;
  text: string;
  order?: number;
}

/**
 * One thing members can expect to gain -- the program's PROMISE.
 *
 * Same shape as ChallengeItem, and stored the same way (an ordered JSONB list
 * on the subtype row), because it is the same kind of thing: a line of program
 * copy. It is never assigned, scored or completed -- members' actual goals are
 * agreed together at Kickoff, once the cohort exists.
 */
export type OutcomeItem = ChallengeItem;

export interface AgreementClause {
  id?: string;
  text: string;
  /** Server-controlled: true only when the text matches a shipped default. */
  is_default?: boolean;
  order?: number;
}

export interface ApplicationQuestion {
  id: number;
  question_text: string;
  is_required: boolean;
  display_order: number;
  is_predefined: boolean;
}

/** A question as sent back up: no `id` means "create this one". */
export interface ApplicationQuestionInput {
  id?: number;
  question_text: string;
  is_required: boolean;
  display_order: number;
}

export interface PeerAdvisoryProgramDetail {
  id: number;
  external_id: string;
  name: string;
  description: string | null;
  status: string;
  program_type: string;

  slug: string | null;
  invite_token: string | null;
  starts_at: string | null;
  ends_at: string | null;
  duration_weeks: number | null;
  weekly_time_commitment: string | null;
  visibility: PeerVisibility | null;

  who_is_this_program_for: string | null;
  audience_criteria: AudienceCriteria | null;
  min_members: number | null;
  max_members: number | null;
  /**
   * An ordered list, not prose. Step 1 of the achiever's application requires
   * each entry to be confirmed individually and records WHICH ones were, so the
   * items carry stable ids -- see PeerApplicationDetail.acknowledged_prerequisite_ids.
   * Was a plain string until the achiever flow needed per-item ticks.
   */
  prerequisites: ChallengeItem[];
  application_deadline: string | null;

  expected_outcomes: OutcomeItem[];
  example_challenges: ChallengeItem[];

  session_mode: PeerSessionMode | null;
  facilitation_mode: PeerFacilitationMode | null;
  group_agreement: AgreementClause[];
  application_questions: ApplicationQuestion[];

  created_by_user_id: string;
  created_at: string;
  updated_at: string;

  members_count: number;
  sessions_count: number;
  pending_applications_count: number;
  facilitators: PeerFacilitatorSummary[];
  price_cents: number | null;

  submitted_at: string | null;
  reviewed_at: string | null;
  rejection_reason: string | null;
}

/** An invitation nobody has answered yet — shown as a pending row. */
export interface PeerPendingInvitation {
  id: number;
  invited_email: string;
  role: string;
  status: string;
}

export interface PeerMembersGrouped {
  facilitators: PeerFacilitatorSummary[];
  peers: PeerFacilitatorSummary[];
  pending_invitations: PeerPendingInvitation[];
}

export interface PeerFacilitatorSummary {
  membership_id: number;
  user_id: string;
  role: string;
  status: string;
  full_name: string | null;
  email: string | null;
  profile_picture_url: string | null;
}

export interface GroupedPeerAdvisoryPrograms {
  active: PeerAdvisoryProgramDetail[];
  draft: PeerAdvisoryProgramDetail[];
  in_review: PeerAdvisoryProgramDetail[];
  rejected: PeerAdvisoryProgramDetail[];
  past: PeerAdvisoryProgramDetail[];
}

/** Step-1 create payload. Only `name` is required. */
export interface PeerAdvisoryProgramCreatePayload {
  name: string;
  description?: string | null;
  duration_weeks?: number;
  weekly_time_commitment?: string;
  visibility?: PeerVisibility;
  /** null is never sent; omit the key to leave the saved price untouched. */
  price_cents?: number;
}

/**
 * One PATCH covers every step of the wizard -- every field optional, omitted
 * keys left untouched. Deliberately not one endpoint per step: it keeps each
 * step to a single round-trip, which is what the connection pool wants.
 */
export interface PeerAdvisoryProgramUpdatePayload {
  name?: string;
  description?: string | null;
  duration_weeks?: number;
  weekly_time_commitment?: string;
  visibility?: PeerVisibility;
  price_cents?: number;
  // No starts_at/ends_at -- both are derived from the sessions and output-only,
  // the same rule Expert Programs use:
  //   starts_at = earliest scheduled GROUP session
  //   ends_at   = latest scheduled GROUP session
  // A single-session group therefore starts and ends the same day. The
  // application_deadline is OPTIONAL and independent of both; the backend only
  // requires that it not fall after the first session.

  who_is_this_program_for?: string | null;
  audience_criteria?: AudienceCriteria;
  min_members?: number;
  max_members?: number;
  /**
   * Replaced wholesale, like the other lists. Send an item's existing `id` back
   * to keep it: the server preserves the ids it is given and mints new ones for
   * items without. That matters here beyond tidiness -- applicants'
   * acknowledged_prerequisite_ids point at these ids, so dropping an id voids
   * every consent recorded against that requirement.
   */
  prerequisites?: ChallengeItem[];
  application_deadline?: string | null;
  application_questions?: ApplicationQuestionInput[];

  // Both are ordered lists of program copy, replaced wholesale by step 3.
  expected_outcomes?: OutcomeItem[];
  example_challenges?: ChallengeItem[];

  session_mode?: PeerSessionMode;
  facilitation_mode?: PeerFacilitationMode;
  group_agreement?: AgreementClause[];
}

export interface PeerApplicationAnswer {
  question_id: number;
  question_text: string;
  is_required: boolean;
  answer_text: string | null;
}

/**
 * One of the four review outcomes, as the API expects it.
 *
 *   ACCEPT       joins the cohort and consumes a seat
 *   WAITLIST     a good fit held for a future cohort; no seat consumed
 *   REQUEST_INFO asks a follow-up, returning the ball to the applicant
 *   REJECT       declined, and final
 */
export type PeerApplicationDecisionPayload = {
  decision: "ACCEPT" | "WAITLIST" | "REQUEST_INFO" | "REJECT";
  /** Required on REJECT. Shown to the applicant. */
  rejection_reason?: string;
  /** Required on REQUEST_INFO. The question they have to answer. */
  info_request_message?: string;
};

/**
 * A goal the applicant proposes bringing to the group, as returned.
 *
 * `id` is an `objective_key_results` row id: a proposal IS an OKR, held without
 * a membership until the application is accepted. There is no separate
 * promotion field, because acceptance sets that membership on this same row.
 *
 * `display_order` is the position in the list, derived server-side from
 * insertion order -- the OKR table has no ordering column of its own.
 */
export interface PeerApplicationGoal {
  id: number;
  title: string;
  description: string | null;
  display_order: number;
  /**
   * True once a facilitator has copied this proposal onto the program's own
   * goal list. Only ever populated on the review queue — the applicant's own
   * form has no business knowing whether their goal was picked.
   */
  in_program_goals?: boolean;
}

/** A goal as sent up. `order` comes from list position, so it is not sent. */
export interface PeerApplicationGoalInput {
  title: string;
  description?: string | null;
}

export interface PeerApplicationSummary {
  id: number;
  program_id: number;
  user_id: string;
  status: PeerApplicationStatus;
  submitted_at: string;
  reviewed_at: string | null;
  rejection_reason: string | null;
  /** The outstanding follow-up question, when status is INFO_REQUESTED. */
  info_request_message: string | null;
  membership_id: number | null;
  full_name: string | null;
  email: string | null;
  profile_picture_url: string | null;
  current_title: string | null;
  current_company: string | null;
  location: string | null;
  /** A band ("0-2", "5-10", "20+"), not a count — render, don't compute. */
  years_of_experience: string | null;
  current_role_level: string | null;
  /** Present only when /profile/{slug} would resolve — see the API comment. */
  public_profile_slug: string | null;
  answers: PeerApplicationAnswer[];

  // Asked of every applicant regardless of the facilitator's own questions, so
  // the review drawer gives each its own labelled section.
  contribution: string | null;
  conflicts_of_interest: string | null;
  goals: PeerApplicationGoal[];

  // The consent record, summarised for display: the reviewer needs to see that
  // the requirements were confirmed, not the id list.
  acknowledged_prerequisite_count: number;
  acknowledged_commitments: string[];
  criteria_acknowledged: boolean;
  agreement_accepted_at: string | null;
}

/**
 * One program's slice of the cross-program review inbox.
 *
 * Grouped by program rather than flat because a decision is made against a
 * cohort: how many places are left is what makes accept-vs-waitlist, and that is
 * a per-program number.
 */
export interface PeerApplicationQueueProgram {
  external_id: string;
  name: string;
  status: string;
  min_members: number | null;
  max_members: number | null;
  application_deadline: string | null;
  /** Seats actually taken -- ACCEPTED applications. */
  places_filled: number;
  applications: PeerApplicationSummary[];
}

/**
 * The caller's own application to one program, as the wizard rehydrates it.
 * Distinct from PeerApplicationSummary, which is the facilitator's view of
 * somebody else's.
 */
export interface PeerMyApplication {
  id: number;
  status: PeerApplicationStatus;
  /** Which step to resume a DRAFT on. */
  last_step: PeerApplyStep | string | null;
  submitted_at: string;
  withdrawn_at: string | null;
  rejection_reason: string | null;
  info_request_message: string | null;
  contribution: string | null;
  conflicts_of_interest: string | null;
  criteria_acknowledged: boolean;
  agreement_accepted: boolean;
  acknowledged_prerequisite_ids: string[];
  acknowledged_commitments: string[];
  answers: { question_id: number; answer_text: string | null }[];
  goals: { id: number; title: string; description: string | null; display_order: number }[];
}

/**
 * Everything the six-step application form renders, in one response.
 *
 * THE form's single source of truth: prerequisites, questions, who-this-is-for
 * and the audience criteria are all the facilitator's own words, so nothing here
 * may be hardcoded in the page or it will silently diverge from the program.
 *
 * `commitments` is the one exception -- a platform-wide constant rather than
 * program data -- and is served here anyway so the client still reads the whole
 * form from one place.
 */
export interface PeerApplicationForm {
  external_id: string;
  name: string;
  description: string | null;
  application_deadline: string | null;
  visibility: PeerVisibility | null;

  // Step 1 — Eligibility & Fit
  prerequisites: ChallengeItem[];
  who_is_this_program_for: string | null;
  audience_criteria: AudienceCriteria | null;

  // Step 2 — Your Application
  questions: Omit<ApplicationQuestion, "is_predefined">[];

  // Step 3 — Goals
  min_goals: number;
  max_goals: number;

  // Step 4 — Group Fit & Availability
  commitments: string[];
  weekly_time_commitment: string | null;
  duration_weeks: number | null;
  min_members: number | null;
  max_members: number | null;
  session_mode: PeerSessionMode | null;

  // Step 5 — Group Agreement
  group_agreement: AgreementClause[];

  // Step 6 — Review & Submit
  facilitators: PeerFacilitatorSummary[];

  /** null on a first visit. */
  my_application: PeerMyApplication | null;
  /** Kept for older callers that read only the status. */
  my_application_status: PeerApplicationStatus | null;
}

/**
 * One step's worth of progress. Every field optional: an omitted key is left
 * alone, so a single PATCH can carry any subset of the six steps -- the same
 * contract PeerAdvisoryProgramUpdatePayload uses on the facilitator side.
 *
 * A list that IS sent replaces its whole set, because each list belongs to
 * exactly one step which owns all of it.
 */
export interface PeerApplicationDraftPayload {
  answers?: { question_id: number; answer_text?: string | null }[];
  goals?: PeerApplicationGoalInput[];
  contribution?: string | null;
  conflicts_of_interest?: string | null;
  acknowledged_prerequisite_ids?: string[];
  acknowledged_commitments?: string[];
  criteria_acknowledged?: boolean;
  /**
   * Whether the agreement is accepted -- never when. The server stamps
   * agreement_accepted_at itself, so a client cannot choose the consent time.
   */
  agreement_accepted?: boolean;
  last_step?: PeerApplyStep;
}

/** The caller's own application, as listed across every program. */
export interface PeerMyApplicationListItem {
  id: number;
  status: PeerApplicationStatus;
  submitted_at: string;
  reviewed_at: string | null;
  rejection_reason: string | null;
  info_request_message: string | null;
  withdrawn_at: string | null;
  last_step: PeerApplyStep | string | null;
  /**
   * Enough of the program to render a card without a second request. The
   * Programs page and the Journey Portfolio both show applications alongside
   * real memberships, and a card needs duration, cadence and who runs the group
   * -- not just a name.
   */
  program: {
    external_id: string;
    name: string;
    description: string | null;
    status: string;
    starts_at: string | null;
    duration_weeks: number | null;
    weekly_time_commitment: string | null;
    min_members: number | null;
    max_members: number | null;
    application_deadline: string | null;
    sessions_count: number;
    facilitators: PeerFacilitatorSummary[];
  };
}

// ── API ───────────────────────────────────────────────────────────────────────

export const peerAdvisoryApi = {
  /** The facilitator's programs, bucketed by the state they can act on. */
  getGrouped: async (): Promise<GroupedPeerAdvisoryPrograms> => {
    const { data } = await authAxios.get(`${PEER_BASE}/facilitator/grouped`);
    return data;
  },

  create: async (
    payload: PeerAdvisoryProgramCreatePayload,
  ): Promise<PeerAdvisoryProgramDetail> => {
    const { data } = await authAxios.post(PEER_BASE, payload);
    return data;
  },

  get: async (externalId: string): Promise<PeerAdvisoryProgramDetail> => {
    const { data } = await authAxios.get(`${PEER_BASE}/${externalId}`);
    return data;
  },

  update: async (
    externalId: string,
    payload: PeerAdvisoryProgramUpdatePayload,
  ): Promise<PeerAdvisoryProgramDetail> => {
    const { data } = await authAxios.patch(`${PEER_BASE}/${externalId}`, payload);
    return data;
  },

  remove: async (externalId: string): Promise<void> => {
    await authAxios.delete(`${PEER_BASE}/${externalId}`);
  },

  submitForReview: async (externalId: string): Promise<PeerAdvisoryProgramDetail> => {
    const { data } = await authAxios.post(`${PEER_BASE}/${externalId}/submit-for-review`);
    return data;
  },

  publish: async (externalId: string): Promise<PeerAdvisoryProgramDetail> => {
    const { data } = await authAxios.post(`${PEER_BASE}/${externalId}/publish`);
    return data;
  },

  getApplications: async (
    externalId: string,
    status?: PeerApplicationStatus,
  ): Promise<PeerApplicationSummary[]> => {
    const query = status ? `?status=${status}` : "";
    const { data } = await authAxios.get(`${PEER_BASE}/${externalId}/applications${query}`);
    return data;
  },

  /**
   * Record a decision on one application.
   *
   * All four outcomes, matching the API's own PeerApplicationDecision — this
   * used to accept only ACCEPT/REJECT, which was a stub: nothing called it, and
   * the server has supported WAITLIST and REQUEST_INFO since it was written.
   *
   * The two message fields are not interchangeable and the API enforces which
   * one it needs:
   *   REJECT       requires `rejection_reason`   — shown to the applicant
   *   REQUEST_INFO requires `info_request_message` — the follow-up question
   * A blank one is a 422, so callers must collect the text before sending.
   */
  reviewApplication: async (
    externalId: string,
    applicationId: number,
    decision: PeerApplicationDecisionPayload,
  ): Promise<{
    id: number;
    status: PeerApplicationStatus;
    membership_id: number | null;
    reviewed_at: string | null;
  }> => {
    const { data } = await authAxios.patch(
      `${PEER_BASE}/${externalId}/applications/${applicationId}`,
      decision,
    );
    return data;
  },

  /**
   * Copy one proposed goal onto the program's goal list, or take it back off.
   *
   * Both directions are idempotent server-side, which is what makes "Add all"
   * safe to fire as N parallel requests rather than a bespoke bulk endpoint —
   * a repeat is a no-op, not a duplicate program goal.
   */
  setGoalInProgramGoals: async (
    externalId: string,
    applicationId: number,
    goalId: number,
    inProgramGoals: boolean,
  ): Promise<void> => {
    const url = `${PEER_BASE}/${externalId}/applications/${applicationId}/goals/${goalId}/program-goal`;
    if (inProgramGoals) await authAxios.post(url);
    else await authAxios.delete(url);
  },

  inviteFacilitator: async (
    externalId: string,
    email: string,
    message?: string,
  ): Promise<{ invited: string[]; skipped: string[]; invalid: string[] }> => {
    const { data } = await authAxios.post(`${PEER_BASE}/${externalId}/facilitators/invite`, {
      email,
      message,
    });
    return data;
  },

  /** Facilitators, peers and the invitations nobody has answered yet. */
  getMembers: async (externalId: string): Promise<PeerMembersGrouped> => {
    const { data } = await authAxios.get(`${PEER_BASE}/${externalId}/members`);
    return data;
  },

  /** Withdraw an invitation before it is answered. */
  cancelInvitation: async (externalId: string, invitationId: number): Promise<void> => {
    await authAxios.delete(`${PEER_BASE}/${externalId}/invitations/${invitationId}`);
  },

  /**
   * The invitee's answer. Accepting is what creates their membership — there is
   * no PENDING row beforehand, so this is the only way into the program.
   *
   * Listing pending invitations is NOT here: that is
   * programsApi.getMyProgramInvitations, one call covering every program type.
   */
  respondToInvitation: async (
    externalId: string,
    invitationId: number,
    accept: boolean,
  ): Promise<{ accepted: boolean; membership_id: number | null; role: string | null }> => {
    const { data } = await authAxios.post(
      `${PEER_BASE}/${externalId}/invitations/${invitationId}/respond`,
      { accept },
    );
    return data;
  },

  /** Remove an accepted facilitator or peer. */
  removeMember: async (externalId: string, membershipId: number): Promise<void> => {
    await authAxios.delete(`${PEER_BASE}/${externalId}/members/${membershipId}`);
  },

  inviteMembers: async (externalId: string, emails: string[], message?: string) => {
    const { data } = await authAxios.post(`${PEER_BASE}/${externalId}/members/invite`, {
      emails,
      message,
    });
    return data;
  },

  // ── The achiever's side ─────────────────────────────────────────────────────

  /**
   * Everything the six-step form renders, plus the caller's own draft.
   *
   * 403 when the caller's Expert Application is not APPROVED, and 404 when the
   * program is not open. Both are states the page renders rather than errors it
   * reports, which is why this opts out of the global 403 redirect: without
   * `_skipForbiddenRedirect` the interceptor hard-navigates to /unauthorized and
   * the page's own "open to approved Experts" explanation -- the one thing that
   * tells the reader what to do about it -- never renders.
   */
  getApplicationForm: async (externalId: string): Promise<PeerApplicationForm> => {
    const { data } = await authAxios.get(`${PEER_BASE}/${externalId}/application-form`, {
      _skipForbiddenRedirect: true,
    } as never);
    return data;
  },

  /**
   * Save one step without submitting. Creates the application as DRAFT on the
   * first call, which is invisible to the facilitator.
   */
  saveApplicationDraft: async (
    externalId: string,
    payload: PeerApplicationDraftPayload,
  ): Promise<{ id: number; status: PeerApplicationStatus; last_step: string | null }> => {
    const { data } = await authAxios.patch(`${PEER_BASE}/${externalId}/application`, payload, {
      _skipForbiddenRedirect: true,
    } as never);
    return data;
  },

  /**
   * Submit. The server saves the payload and then validates the RESULTING
   * application, so a draft built up over several visits can be submitted by
   * sending only the last step -- or `{}`.
   *
   * 422 carries a `detail` naming the first incomplete step, which is the right
   * thing to surface verbatim.
   */
  applyToProgram: async (
    externalId: string,
    payload: PeerApplicationDraftPayload = {},
  ): Promise<{ id: number; status: PeerApplicationStatus; submitted_at: string }> => {
    // Same opt-out as the form: approval can be revoked mid-application, and
    // that should surface as a message on the page they are standing on, not a
    // hard bounce that loses their work.
    const { data } = await authAxios.post(`${PEER_BASE}/${externalId}/apply`, payload, {
      _skipForbiddenRedirect: true,
    } as never);
    return data;
  },

  /**
   * Withdraw before a decision. Reversible -- the answers and goals survive, so
   * re-submitting revives the same application. This is what backs "Edit
   * application" on the submitted screen.
   */
  withdrawApplication: async (
    externalId: string,
  ): Promise<{ id: number; status: PeerApplicationStatus; withdrawn_at: string | null }> => {
    const { data } = await authAxios.post(`${PEER_BASE}/${externalId}/application/withdraw`);
    return data;
  },

  /** The caller's own applications, across every program. */
  myApplications: async (): Promise<PeerMyApplicationListItem[]> => {
    const { data } = await authAxios.get(`${PEER_BASE}/applications/me`);
    return data;
  },

  /**
   * Every application across the caller's facilitated programs, grouped by
   * program -- the top-level "Applications" screen, whose program selector can
   * be cleared to show all of them at once.
   *
   * Every facilitated program is listed even with nothing waiting, so the
   * selector can show the full set.
   */
  getReviewQueue: async (
    statusFilter?: PeerApplicationStatus,
  ): Promise<PeerApplicationQueueProgram[]> => {
    const { data } = await authAxios.get(`${PEER_BASE}/applications/queue`, {
      params: statusFilter ? { status: statusFilter } : undefined,
    });
    return data;
  },
};
